<script lang="ts">
  import { untrack } from 'svelte';
  import { Building2, Blocks, Ship, Lightbulb, ShoppingBag, Handshake } from 'lucide-svelte';
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import ProjectPicker from '$lib/components/projects/ProjectPicker.svelte';
  import { quotationsApi } from '$lib/api/quotations';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import {
    QUOTE_VERTICALS,
    QUOTE_VERTICAL_META,
    BILLING_ENTITIES,
    BILLING_ENTITY_LABEL,
    type QuoteVertical,
    type BillingEntity,
    type QuotationDetail
  } from '$lib/types';

  const { onClose, onCreated, projectId = null }: { onClose: () => void; onCreated: (q: QuotationDetail) => void; projectId?: number | null } = $props();
  let selectedProject = $state<number | null>(untrack(() => projectId));
  const queryClient = useQueryClient();

  const CURRENCY_BY_VERTICAL: Record<QuoteVertical, string> = {
    hotel: 'INR',
    builder: 'INR',
    export: 'USD',
    lighting: 'INR',
    retail: 'INR',
    dealers: 'INR'
  };
  // "MD-priced" / "Self-serve" labels mirror internal/lifecycle.DesignerPriced:
  // hotel/builder are always MD-priced; lighting/retail/dealers are always
  // self-serve; export depends on project_based (surfaced as its own step).
  const MODEL_LABEL: Record<QuoteVertical, string> = {
    hotel: 'MD-priced',
    builder: 'MD-priced',
    export: 'MD / self',
    lighting: 'Self-serve',
    retail: 'Self-serve',
    dealers: 'Self-serve'
  };

  const VERTICAL_ICON: Record<QuoteVertical, typeof Building2> = {
    hotel: Building2,
    builder: Blocks,
    export: Ship,
    lighting: Lightbulb,
    retail: ShoppingBag,
    dealers: Handshake
  };

  const accessible = $derived(
    session.isAdmin ? new Set(QUOTE_VERTICALS) : session.accessibleQuoteVerticals
  );

  let pendingExport = $state(false);
  let error = $state('');
  // Billing entity (PDF issuer) — admin-only (a designer's submitted value is
  // ignored server-side, same as everywhere else this field is set), defaults
  // to "controls" same as the schema default.
  let billingEntity = $state<BillingEntity>('controls');

  const createMut = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) => quotationsApi.create(body),
    onSuccess: (q) => {
      queryClient.invalidateQueries({ queryKey: ['pipeline-quotations'] });
      queryClient.invalidateQueries({ queryKey: ['project-quotations'] });
      onCreated(q);
    },
    onError: (err: unknown) => (error = getErrorMessage(err))
  }));

  function pick(vertical: QuoteVertical) {
    error = '';
    if (vertical === 'export') {
      pendingExport = true;
      return;
    }
    createMut.mutate({ vertical, currency: CURRENCY_BY_VERTICAL[vertical], billing_entity: billingEntity, project_id: selectedProject });
  }

  function pickExport(projectBased: boolean) {
    error = '';
    createMut.mutate({
      vertical: 'export',
      currency: CURRENCY_BY_VERTICAL.export,
      project_based: projectBased,
      project_id: selectedProject,
      billing_entity: billingEntity
    });
  }
</script>

<Modal title={pendingExport ? 'Export quotation' : 'Start a new quotation'} onClose={onClose} wide>
  <div class="mb-5"><ProjectPicker value={selectedProject} onChange={(id) => (selectedProject = id)} disabled={createMut.isPending} /></div>
  {#if pendingExport}
    <p class="text-[13.5px] text-dark-gray mb-5">
      Export orders are either a standalone self-serve order, or part of an MD-priced project — pick one.
    </p>
    {#if session.isAdmin}
      <label class="flex items-center justify-between gap-3 mb-5 text-[13px]">
        <span class="font-medium text-navy">Billing entity</span>
        <select class="pq-num-input w-auto" bind:value={billingEntity}>
          {#each BILLING_ENTITIES as entity (entity)}
            <option value={entity}>{BILLING_ENTITY_LABEL[entity]}</option>
          {/each}
        </select>
      </label>
    {/if}
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
      <button
        type="button"
        class="pq-vertical-card"
        disabled={createMut.isPending}
        onclick={() => pickExport(false)}
      >
        <div class="font-heading font-bold text-[15px] text-navy">Standalone order</div>
        <div class="text-[12px] text-dark-gray leading-relaxed">
          Self-serve — you set prices and approve directly, no MD pricing step.
        </div>
      </button>
      <button
        type="button"
        class="pq-vertical-card"
        disabled={createMut.isPending}
        onclick={() => pickExport(true)}
      >
        <div class="font-heading font-bold text-[15px] text-navy">Project-based</div>
        <div class="text-[12px] text-dark-gray leading-relaxed">
          MD-priced — submit your BOQ and the admin adds pricing before approval.
        </div>
      </button>
    </div>
    {#if error}<p class="text-sm text-red-600 mt-3">{error}</p>{/if}
    <button type="button" class="btn-secondary mt-4" onclick={() => (pendingExport = false)}>Back</button>
  {:else}
    <p class="text-[13.5px] text-dark-gray mb-5">
      Pick a vertical — it sets the currency, tax, and pricing workflow for the whole quote.
    </p>
    {#if session.isAdmin}
      <label class="flex items-center justify-between gap-3 mb-5 text-[13px]">
        <span class="font-medium text-navy">Billing entity</span>
        <select class="pq-num-input w-auto" bind:value={billingEntity}>
          {#each BILLING_ENTITIES as entity (entity)}
            <option value={entity}>{BILLING_ENTITY_LABEL[entity]}</option>
          {/each}
        </select>
      </label>
    {/if}
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
      {#each QUOTE_VERTICALS as v (v)}
        {@const meta = QUOTE_VERTICAL_META[v]}
        {@const Icon = VERTICAL_ICON[v]}
        {@const canPick = accessible.has(v)}
        <button
          type="button"
          class="pq-vertical-card"
          disabled={!canPick || createMut.isPending}
          title={canPick ? '' : "You don't have access to this vertical"}
          onclick={() => pick(v)}
        >
          <div class="w-11 h-11 rounded-[11px] bg-toyama-teal/10 flex items-center justify-center">
            {#if Icon}<Icon class="h-[22px] w-[22px] text-toyama-teal" />{/if}
          </div>
          <div>
            <div class="font-heading font-bold text-[16px] text-navy">{meta.label}</div>
            <div class="text-[12px] text-dark-gray mt-0.5 leading-snug">{meta.blurb}</div>
          </div>
          <div class="flex gap-1.5 flex-wrap mt-0.5">
            <span class="text-[10.5px] font-semibold text-navy bg-toyama-light-carbon border border-toyama-cloudy-gray rounded-[6px] px-[7px] py-0.5">
              {CURRENCY_BY_VERTICAL[v]}
            </span>
            <span class="text-[10.5px] font-semibold text-toyama-teal bg-toyama-teal/10 rounded-[6px] px-[7px] py-0.5">
              {MODEL_LABEL[v]}
            </span>
          </div>
        </button>
      {/each}
    </div>
    {#if error}<p class="text-sm text-red-600 mt-3">{error}</p>{/if}
  {/if}
</Modal>
